import { useUpdateOrganizationMutation } from '@consigli/hooks';
import { Button } from '@consigli/shadcn';
import { Organization } from '@consigli/types';
import { FC, useCallback, useState } from 'react';

import { SheetForm } from '@/components';
import { errorToast, successToast } from '@/components/toast';

import { AvailableServicesFields } from './available-services-fields';
import { EditOrganizationFormData, FormErrors } from './types';
import { validateEditOrganizationForm } from './validation';

interface EditOrganizationFormProps {
  organization: Organization;
  trigger?: React.ReactNode;
  onSuccess?: () => void;
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
}

export const EditOrganizationForm: FC<EditOrganizationFormProps> = ({
  organization,
  trigger,
  onSuccess,
  open: controlledOpen,
  onOpenChange: controlledOnOpenChange,
}) => {
  const [internalOpen, setInternalOpen] = useState(false);

  // Support both controlled and uncontrolled modes
  const isControlled = controlledOpen !== undefined;
  const isOpen = isControlled ? controlledOpen : internalOpen;

  const setIsOpen = useCallback(
    (open: boolean) => {
      if (isControlled) {
        controlledOnOpenChange?.(open);
      } else {
        setInternalOpen(open);
      }
    },
    [isControlled, controlledOnOpenChange],
  );

  const [errors, setErrors] = useState<FormErrors>({});
  const [formData, setFormData] = useState<EditOrganizationFormData>({
    availableServices: organization.availableServices || [],
  });

  const [updateOrganization, { isLoading }] = useUpdateOrganizationMutation();

  const resetForm = useCallback(() => {
    setFormData({
      availableServices: organization.availableServices || [],
    });
    setErrors({});
  }, [organization]);

  const handleFieldChange = useCallback(
    (field: keyof EditOrganizationFormData, value: unknown) => {
      setFormData((prev) => ({ ...prev, [field]: value }));
      // Clear error when field is updated
      if (errors[field]) {
        setErrors((prev) => ({ ...prev, [field]: undefined }));
      }
    },
    [errors],
  );

  const handleSubmit = useCallback(async (): Promise<boolean> => {
    const validation = validateEditOrganizationForm(formData);

    if (!validation.isValid) {
      setErrors(validation.errors);
      return false;
    }

    try {
      await updateOrganization({
        organizationId: organization.id,
        data: {
          availableServices: formData.availableServices,
        },
      }).unwrap();

      successToast({ message: 'Organization updated successfully!' });
      setIsOpen(false);
      onSuccess?.();
      return true;
    } catch (error) {
      console.error('Failed to update organization:', error);
      errorToast({ message: 'Failed to update organization. Please try again.' });
      return false;
    }
  }, [formData, organization.id, updateOrganization, onSuccess, setIsOpen]);

  const handleOpenChange = useCallback(
    (open: boolean) => {
      setIsOpen(open);
      if (open) {
        // Reset to current organization values when opening
        resetForm();
      }
    },
    [resetForm, setIsOpen],
  );

  return (
    <SheetForm
      title={`Edit ${organization.name}`}
      description="Update organization settings and available services."
      trigger={trigger || <Button variant="outline">Edit</Button>}
      submitLabel="Save Changes"
      cancelLabel="Cancel"
      onSubmit={handleSubmit}
      onCancel={resetForm}
      isSubmitting={isLoading}
      open={isOpen}
      onOpenChange={handleOpenChange}
    >
      <div className="space-y-6">
        <AvailableServicesFields
          formData={formData}
          errors={errors}
          onFieldChange={handleFieldChange}
        />
      </div>
    </SheetForm>
  );
};
